<template>
  <div class="page-view">
    <BasePageHeader :title="pageTitle" @back="$router.push({ name: 'AdminUsers' })">
      <template #actions>
        <template v-if="isNew">
          <button class="btn primary" :disabled="saving || !form.email" @click="save">
            {{ saving ? $t('common.saving') : $t('tenantUserDetail.sendInvite') }}
          </button>
        </template>
        <template v-else-if="!loading && user">
          <button v-if="!editing" class="btn ghost sm" @click="editing = true">{{ $t('common.edit') }}</button>
          <template v-else>
            <button class="btn ghost sm" @click="cancelEdit">{{ $t('common.cancel') }}</button>
            <button class="btn primary sm" :disabled="saving || !form.email" @click="save">
              {{ saving ? $t('common.saving') : $t('common.save') }}
            </button>
          </template>
        </template>
      </template>
    </BasePageHeader>

    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <EmptyState v-else-if="loadError" variant="error" :message="loadError" />

    <template v-else>
      <!-- Basic Info Card -->
      <div class="mx-4 my-4 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden">
        <div class="px-5 py-3 border-b border-[var(--color-border)]">
          <h2 class="section-label m-0">{{ $t('tenantUserDetail.basicInfo') }}</h2>
        </div>

        <template v-if="!editing && !isNew">
          <!-- View mode -->
          <div class="flex items-center gap-3 px-5 py-3 border-b border-[var(--color-border)]">
            <span class="w-28 shrink-0 text-xs font-semibold text-[var(--color-muted)] uppercase tracking-wide">{{ $t('common.name') }}</span>
            <span class="flex-1 text-sm text-[var(--color-text)]">{{ fullName || '—' }}</span>
            <span v-if="!user.is_active" class="badge badge-danger">{{ $t('common.inactive') }}</span>
          </div>
          <div class="flex items-center gap-3 px-5 py-3 border-b border-[var(--color-border)]">
            <span class="w-28 shrink-0 text-xs font-semibold text-[var(--color-muted)] uppercase tracking-wide">{{ $t('tenantUserDetail.email') }}</span>
            <a :href="`mailto:${user.email}`" class="flex-1 text-sm text-blue-400 no-underline hover:underline">{{ user.email }}</a>
          </div>
          <div class="flex items-center gap-3 px-5 py-3 border-b border-[var(--color-border)]">
            <span class="w-28 shrink-0 text-xs font-semibold text-[var(--color-muted)] uppercase tracking-wide">{{ $t('tenantUserDetail.phone') }}</span>
            <span class="flex-1 text-sm text-[var(--color-text)]">{{ user.profile?.phone || '—' }}</span>
          </div>
          <div class="flex items-center gap-3 px-5 py-3">
            <span class="w-28 shrink-0 text-xs font-semibold text-[var(--color-muted)] uppercase tracking-wide">{{ $t('tenantUserDetail.badgeTag') }}</span>
            <span class="flex-1 text-sm text-[var(--color-text)]">{{ user.profile?.unique_tag || '—' }}</span>
          </div>
        </template>

        <template v-else>
          <!-- Edit / create mode -->
          <div class="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4 px-5 py-4 border-b border-[var(--color-border)]">
            <BaseFormField :label="$t('tenantUserDetail.firstName')">
              <BaseInput v-model="form.first_name" placeholder="Ola" maxlength="150" autocomplete="given-name" />
            </BaseFormField>
            <BaseFormField :label="$t('tenantUserDetail.lastName')">
              <BaseInput v-model="form.last_name" placeholder="Nordmann" maxlength="150" autocomplete="family-name" />
            </BaseFormField>
          </div>
          <div class="px-5 py-4 border-b border-[var(--color-border)]">
            <BaseFormField :label="$t('tenantUserDetail.emailAddress')" required :hint="isNew ? $t('tenantUserDetail.inviteEmailHint') : ''">
              <BaseInput v-model="form.email" type="email" placeholder="ola@eksempel.no" maxlength="254" autocomplete="email" />
            </BaseFormField>
          </div>
          <div v-if="!isNew" class="px-5 py-4 border-b border-[var(--color-border)]">
            <BaseFormField :label="$t('tenantUserDetail.phoneNumber')">
              <BaseInput v-model="form.phone" type="tel" placeholder="+47 123 45 678" maxlength="20" autocomplete="tel" />
            </BaseFormField>
          </div>
          <div v-if="!isNew" class="px-5 py-4 border-b border-[var(--color-border)]">
            <BaseFormField :label="$t('tenantUserDetail.badgeTagId')">
              <BaseInput v-model="form.unique_tag" :placeholder="$t('ui.badge00123')" maxlength="120" autocomplete="off" />
            </BaseFormField>
          </div>
          <div v-if="!isNew" class="px-5 py-4">
            <BaseFormField :label="$t('tenantUserDetail.activeAccount')">
              <BaseToggle v-model="form.is_active" :label="$t('tenantUserDetail.activeAccount')" />
            </BaseFormField>
          </div>
        </template>
      </div>

      <!-- Roles Card -->
      <div class="mx-4 mb-4 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden">
        <div class="flex items-center gap-3 px-5 py-3 border-b border-[var(--color-border)]">
          <h2 class="section-label flex-1 m-0">{{ $t('tenantUserDetail.roles') }}</h2>
          <template v-if="!isNew">
            <button v-if="!editingRoles" class="btn ghost sm" @click="startEditRoles">{{ $t('common.edit') }}</button>
            <template v-else>
              <button class="btn ghost sm" @click="cancelEditRoles">{{ $t('common.cancel') }}</button>
              <button class="btn primary sm" :disabled="rolesSaving" @click="saveRoles">
                {{ rolesSaving ? $t('common.saving') : $t('common.save') }}
              </button>
            </template>
          </template>
        </div>

        <div v-if="rolesLoading" class="px-5 py-4 text-sm text-[var(--color-muted)]">{{ $t('tenantUserDetail.loadingRoles') }}</div>
        <div v-else-if="rolesError" class="px-5 py-4 text-sm text-red-400">{{ rolesError }}</div>
        <div v-else class="px-5 py-4 flex flex-col gap-2.5">
          <!-- Invite mode: checkboxes always visible, no edit toggle -->
          <template v-if="isNew">
            <div v-if="!availableRoles.length" class="text-sm text-[var(--color-muted)]">{{ $t('tenantUserDetail.noRoles') }}</div>
            <div v-for="r in availableRoles" :key="r.id"
              class="flex items-center gap-3 px-4 py-3 bg-[var(--color-surface-2)] border border-[var(--color-border)] rounded-lg transition-all hover:border-gray-600">
              <BaseCheckbox :value="r.id" v-model="rolesSelected" :label="r.name" />
            </div>
          </template>
          <!-- Existing user: view/edit toggle -->
          <template v-else-if="!editingRoles">
            <div v-if="assignedRoles.length" class="flex flex-wrap gap-2">
              <span v-for="r in assignedRoles" :key="r.id" class="badge badge-accent">{{ r.name }}</span>
            </div>
            <span v-else class="text-sm text-[var(--color-muted)]">{{ $t('tenantUserDetail.noRolesAssigned') }}</span>
          </template>
          <template v-else>
            <div v-if="!availableRoles.length" class="text-sm text-[var(--color-muted)]">{{ $t('tenantUserDetail.noRoles') }}</div>
            <div v-for="r in availableRoles" :key="r.id"
              class="flex items-center gap-3 px-4 py-3 bg-[var(--color-surface-2)] border border-[var(--color-border)] rounded-lg transition-all hover:border-gray-600">
              <BaseCheckbox :value="r.id" v-model="rolesSelected" :label="r.name" />
            </div>
          </template>
        </div>
      </div>

      <!-- Location Permissions Card (existing users only) -->
      <div v-if="!isNew" class="mx-4 mb-4 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden">
        <div class="px-5 py-3 border-b border-[var(--color-border)]">
          <h2 class="section-label flex-1 m-0">{{ $t('tenantUserDetail.locationPermissions') }}</h2>
        </div>

        <!-- Scope info banner -->
        <div v-if="effectiveLocationScope === 'all'"
          class="flex items-center gap-2 px-5 py-3 text-sm border-b border-[var(--color-border)]"
          style="color: var(--color-accent); background: rgba(168,208,193,0.07)">
          <MdiInformationOutline class="shrink-0 w-4 h-4" />
          {{ $t('tenantUserDetail.allLocationsInfo') }}
        </div>
        <div v-else-if="effectiveLocationScope === 'none'"
          class="flex items-center gap-2 px-5 py-3 text-sm border-b border-[var(--color-border)]"
          style="color: var(--color-muted)">
          <MdiInformationOutline class="shrink-0 w-4 h-4" />
          {{ $t('tenantUserDetail.noLocationAccess') }}
        </div>

        <div v-if="locationPermsLoading" class="px-5 py-4 text-sm text-[var(--color-muted)]">{{ $t('tenantUserDetail.loadingLocations') }}</div>
        <div v-else-if="locationPermsError" class="px-5 py-4 text-sm text-red-400">{{ locationPermsError }}</div>
        <div v-else-if="!locationPerms.length" class="px-5 py-4 text-sm text-[var(--color-muted)]">{{ $t('tenantUserDetail.noLocations') }}</div>
        <div v-else class="divide-y divide-[var(--color-border)]">
          <div v-for="row in locationPerms" :key="row.location.id"
            class="flex items-center gap-3 px-5 py-3">
            <span class="flex-1 text-sm text-[var(--color-text)]">{{ row.location.name }}</span>
            <BaseSelect
              :model-value="row.permission_level"
              :disabled="!!locationPermSaving[row.location.id] || effectiveLocationScope !== 'assigned'"
              class="text-sm py-1.5 w-36"
              density="compact"
              @update:model-value="row.permission_level = $event; saveLocationPerm(row)"
            >
              <option value="none">{{ $t('tenantUserDetail.permNone') }}</option>
              <option value="view">{{ $t('tenantUserDetail.permView') }}</option>
              <option value="change">{{ $t('tenantUserDetail.permChange') }}</option>
              <option value="full">{{ $t('tenantUserDetail.permFull') }}</option>
            </BaseSelect>
            <span class="w-14 text-right text-xs text-[var(--color-muted)]">
              {{ locationPermSaving[row.location.id] ? $t('common.saving') : '' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Danger Zone (existing user only) -->
      <div v-if="!isNew" class="mx-4 mb-4 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden">
        <div class="px-5 py-3 border-b border-[var(--color-border)]">
          <h2 class="section-label danger m-0">{{ $t('common.dangerZone') }}</h2>
        </div>
        <div class="px-5 py-4 flex flex-col gap-3">
          <!-- Set password -->
          <div class="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p class="text-sm font-medium text-[var(--color-text)] m-0">{{ $t('tenantUserDetail.setPassword') }}</p>
              <p class="text-xs text-[var(--color-muted)] mt-0.5 m-0">{{ $t('tenantUserDetail.setPasswordDesc') }}</p>
            </div>
            <button class="btn ghost sm" @click="showPwForm = !showPwForm">{{ $t('tenantUserDetail.setPassword') }}</button>
          </div>
          <div v-if="showPwForm" class="flex flex-col gap-2">
              <BaseFormField :label="$t('tenantUserDetail.setPassword')">
                <BaseInput v-model="pwValue" type="password" :placeholder="$t('tenantUserDetail.pwPlaceholder')"
                autocomplete="new-password" @keyup.enter="doSetPassword" />
              </BaseFormField>
            <div v-if="pwError" class="text-xs text-red-400">{{ pwError }}</div>
            <div class="flex gap-2">
              <button class="btn ghost sm" @click="cancelPwForm">{{ $t('common.cancel') }}</button>
              <button class="btn primary sm" :disabled="pwSaving || pwValue.length < 8" @click="doSetPassword">
                {{ pwSaving ? $t('common.saving') : $t('common.confirm') }}
              </button>
            </div>
          </div>

          <div class="h-px bg-[var(--color-border)]"></div>

          <!-- Deactivate / activate -->
          <div class="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p class="text-sm font-medium text-[var(--color-text)] m-0">{{ user?.is_active ? $t('tenantUserDetail.deactivateUser') : $t('tenantUserDetail.activateUser') }}</p>
              <p class="text-xs text-[var(--color-muted)] mt-0.5 m-0">{{ user?.is_active ? $t('tenantUserDetail.preventLogin') : $t('tenantUserDetail.allowLogin') }}</p>
            </div>
            <button class="btn ghost sm" :disabled="togglingActive" @click="toggleActive">
              {{ togglingActive ? $t('common.saving') : (user?.is_active ? $t('tenantUserDetail.deactivate') : $t('tenantUserDetail.activate')) }}
            </button>
          </div>

          <div class="h-px bg-[var(--color-border)]"></div>

          <!-- Remove from tenant -->
          <div class="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p class="text-sm font-medium text-red-400 m-0">{{ $t('tenantUserDetail.removeFromTenant') }}</p>
              <p class="text-xs text-[var(--color-muted)] mt-0.5 m-0">{{ $t('tenantUserDetail.removeDesc') }}</p>
            </div>
            <button class="btn danger sm" :disabled="removingMembership || isSelf" @click="confirmRemove">
              {{ removingMembership ? $t('tenantUserDetail.removing') : $t('common.remove') }}
            </button>
          </div>


        </div>
      </div>
    </template>
  </div>

  <!-- Remove from tenant confirm modal -->
  <BaseConfirmModal
    :show="showRemoveModal"
    :title="$t('tenantUserDetail.removeTitle')"
    :body="$t('ui.revokeAccess', { name: fullName || user?.email })"
    :confirm-label="$t('common.remove')"
    :loading="removingMembership"
    @confirm="doRemoveMembership"
    @cancel="showRemoveModal = false"
  />


</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from '@/composables/useToast'
import { useAuthStore } from '@/stores/auth.js'
import {
  getUser, inviteUser, updateUser, removeUserMembership,
  adminSetUserPassword, adminGetUserRoles, adminSetUserRoles,
  getTenantRoles, adminGetUserLocationPermissions, adminSetLocationPermission,
} from '@/api/api.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseToggle from '@/components/base/BaseToggle.vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseConfirmModal from '@/components/base/BaseConfirmModal.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import MdiInformationOutline from '~icons/mdi/information-outline'

const route  = useRoute()
const router = useRouter()
const toast  = useToast()
const { t } = useI18n()
const authStore = useAuthStore()

const isNew = computed(() => route.params.userId === 'new')
const isSelf = computed(() => authStore.user?.id === user.value?.id)

let tenantSlug = ''
watch(() => authStore.currentTenant?.slug, (slug) => { tenantSlug = slug ?? '' }, { immediate: true })

// ── state ──────────────────────────────────────────────────────────────────
const user      = ref(null)
const loading   = ref(true)
const loadError = ref(null)
const editing   = ref(false)
const saving    = ref(false)

const emptyForm = () => ({ first_name: '', last_name: '', email: '', phone: '', unique_tag: '', is_active: true })
const form = ref(emptyForm())

// roles
const availableRoles       = ref([])
const rolesSelected        = ref([])
const rolesSelectedBackup  = ref([])
const rolesLoading         = ref(false)
const rolesSaving          = ref(false)
const rolesError           = ref('')
const editingRoles         = ref(false)

// password
const showPwForm = ref(false)
const pwValue    = ref('')
const pwSaving   = ref(false)
const pwError    = ref('')

// remove membership
const showRemoveModal    = ref(false)
const removingMembership = ref(false)

// active toggle
const togglingActive = ref(false)

// location permissions
const locationPerms       = ref([])
const locationPermsLoading = ref(false)
const locationPermsError  = ref('')
const locationPermSaving  = ref({}) // { [locationId]: boolean }

// ── computed ───────────────────────────────────────────────────────────────
const pageTitle = computed(() => {
  if (isNew.value) return t('tenantUserDetail.newUser')
  return fullName.value || user.value?.email || t('macros.inputTypeUser')
})

const fullName = computed(() => {
  const u = user.value
  if (!u) return ''
  return [u.first_name, u.last_name].filter(Boolean).join(' ')
})

const assignedRoles = computed(() =>
  availableRoles.value.filter(r => rolesSelected.value.includes(r.id))
)

const effectiveLocationScope = computed(() => {
  const scopes = assignedRoles.value.map(r => r.location_access_scope)
  if (scopes.includes('all')) return 'all'
  if (scopes.includes('assigned')) return 'assigned'
  if (scopes.length > 0) return 'none'
  return 'none'
})

// ── data loading ───────────────────────────────────────────────────────────
async function load() {
  if (isNew.value) {
    loading.value = false
    editing.value = true
    form.value = emptyForm()
    rolesSelected.value = []
    rolesLoading.value = true
    try {
      availableRoles.value = await getTenantRoles(tenantSlug) ?? []
    } catch {
      rolesError.value = t('tenantUserDetail.rolesLoadFailed')
    } finally {
      rolesLoading.value = false
    }
    return
  }
  loading.value = true
  loadError.value = null
  try {
    user.value = await getUser(route.params.userId)
    applyFormFromUser()
  } catch {
    loadError.value = t('tenantUserDetail.loadFailed')
  } finally {
    loading.value = false
  }
}

async function fetchRoles() {
  rolesLoading.value = true
  rolesError.value = ''
  try {
    const [all, current] = await Promise.all([
      getTenantRoles(tenantSlug),
      adminGetUserRoles(tenantSlug, route.params.userId),
    ])
    availableRoles.value  = all ?? []
    rolesSelected.value   = (current ?? []).map(r => r.id)
  } catch {
    rolesError.value = t('tenantUserDetail.rolesLoadFailed')
  } finally {
    rolesLoading.value = false
  }
}

function applyFormFromUser() {
  const u = user.value
  form.value = {
    first_name: u.first_name ?? '',
    last_name:  u.last_name  ?? '',
    email:      u.email      ?? '',
    phone:      u.profile?.phone      ?? '',
    unique_tag: u.profile?.unique_tag ?? '',
    is_active:  u.is_active  ?? true,
  }
}

// ── edit user info ─────────────────────────────────────────────────────────
function cancelEdit() {
  applyFormFromUser()
  editing.value = false
}

async function save() {
  saving.value = true
  try {
    if (isNew.value) {
      const payload = {
        first_name: form.value.first_name,
        last_name:  form.value.last_name,
        email:      form.value.email,
      }
      if (rolesSelected.value.length) payload.roles = rolesSelected.value
      const invited = await inviteUser(payload)
      toast.success(t('tenantUserDetail.inviteSent'))
      router.replace({ name: 'AdminUserDetail', params: { userId: invited.id } })
    } else {
      const payload = {
        first_name: form.value.first_name,
        last_name:  form.value.last_name,
        email:      form.value.email,
        is_active:  form.value.is_active,
        profile: {
          phone:      form.value.phone,
          unique_tag: form.value.unique_tag,
        },
      }
      user.value = await updateUser(user.value.id, payload)
      applyFormFromUser()
      editing.value = false
      toast.success(t('tenantUserDetail.updated'))
    }
  } catch (e) {
    const msg = e.data?.roles ?? e.data?.email?.[0] ?? e.data?.non_field_errors?.[0] ?? e.data?.detail ?? t('tenantUserDetail.saveFailed')
    toast.error(msg)
  } finally {
    saving.value = false
  }
}

// ── roles ──────────────────────────────────────────────────────────────────
function startEditRoles() {
  rolesSelectedBackup.value = [...rolesSelected.value]
  editingRoles.value = true
}

function cancelEditRoles() {
  rolesSelected.value = [...rolesSelectedBackup.value]
  editingRoles.value = false
}

async function saveRoles() {
  rolesSaving.value = true
  rolesError.value = ''
  try {
    await adminSetUserRoles(tenantSlug, user.value.id, rolesSelected.value)
    rolesSelectedBackup.value = [...rolesSelected.value]
    editingRoles.value = false
    toast.success(t('tenantUserDetail.rolesUpdated'))
    // Refresh location permissions since scope may have changed
    fetchLocationPermissions()
  } catch (e) {
      rolesError.value = e.data?.detail || t('tenantUserDetail.rolesFailed')
  } finally {
    rolesSaving.value = false
  }
}

// ── location permissions ───────────────────────────────────────────────────
async function fetchLocationPermissions() {
  locationPermsLoading.value = true
  locationPermsError.value = ''
  try {
    locationPerms.value = await adminGetUserLocationPermissions(tenantSlug, route.params.userId) ?? []
  } catch {
    locationPermsError.value = t('tenantUserDetail.locationPermLoadFailed')
  } finally {
    locationPermsLoading.value = false
  }
}

async function saveLocationPerm(row) {
  locationPermSaving.value = { ...locationPermSaving.value, [row.location.id]: true }
  try {
    await adminSetLocationPermission(tenantSlug, user.value.id, row.location.id, row.permission_level)
    toast.success(`${row.location.name}: ${t('tenantUserDetail.locationPermUpdated')}`)
  } catch (e) {
    toast.error(e.data?.detail ?? t('tenantUserDetail.locationPermFailed'))
    // Revert optimistic change
    await fetchLocationPermissions()
  } finally {
    const updated = { ...locationPermSaving.value }
    delete updated[row.location.id]
    locationPermSaving.value = updated
  }
}

// ── password ───────────────────────────────────────────────────────────────
function cancelPwForm() {
  showPwForm.value = false
  pwValue.value = ''
  pwError.value = ''
}

async function doSetPassword() {
  if (pwValue.value.length < 8) return
  pwSaving.value = true
  pwError.value = ''
  try {
    await adminSetUserPassword(tenantSlug, user.value.id, pwValue.value)
    toast.success(t('tenantUserDetail.passwordUpdated'))
    cancelPwForm()
  } catch (e) {
    pwError.value = e.data?.new_password?.[0] ?? e.data?.detail ?? t('tenantUserDetail.saveFailed')
  } finally {
    pwSaving.value = false
  }
}

// ── activate / deactivate ──────────────────────────────────────────────────
async function toggleActive() {
  togglingActive.value = true
  try {
    user.value = await updateUser(user.value.id, { is_active: !user.value.is_active })
    applyFormFromUser()
    toast.success(user.value.is_active ? t('tenantUserDetail.activated') : t('tenantUserDetail.deactivated'))
  } catch {
    toast.error(t('tenantUserDetail.updateFailed'))
  } finally {
    togglingActive.value = false
  }
}

// ── remove membership ──────────────────────────────────────────────────────
function confirmRemove() {
  showRemoveModal.value = true
}

async function doRemoveMembership() {
  removingMembership.value = true
  try {
    await removeUserMembership(user.value.id)
    toast.success(t('tenantUserDetail.removed'))
    router.push({ name: 'AdminUsers' })
  } catch (e) {
    toast.error(e.data?.detail ?? t('tenantUserDetail.removeFailed'))
    showRemoveModal.value = false
  } finally {
    removingMembership.value = false
  }
}

// ── init ───────────────────────────────────────────────────────────────────
onMounted(async () => {
  await load()
  if (!isNew.value) {
    fetchRoles()
    fetchLocationPermissions()
  }
})
</script>
